import NeptuneElement from "../../NeptuneElement.js";

class Input extends NeptuneElement {

    static INPUT_TYPES = ['text', 'textarea', 'password', 'email', 'number', 'search', 'tel', 'url', 'date', 'month', 'week', 'time'];
    static MIN_MAX_STEP_TYPES = ['number', 'date', 'month', 'week', 'time'];
    static MAX_LENGTH_TYPES = ['text', 'textarea', 'password', 'email', 'search', 'tel', 'url'];
    static PATTERN_TYPES = ['text', 'search', 'url', 'tel', 'email', 'password'];
    static PLACEHOLDER_TYPES = ['text', 'search', 'url', 'tel', 'email', 'password', 'number'];
    static DEFAULT_TEL_PATTERN = '[+]?[(]?[0-9]{1,4}[)]?[0-9\\s.-]{6,19}';
    static DEFAULT_URL_PATTERN = 'https?:\\/\\/[^\\s]+';
    static TEL_VALUE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[0-9\s.-]{6,19}$/;
    static URL_HTTP_REGEX = /^https?:\/\/[^\s]+$/i;

    static INPUT_TEMPLATE = `
        <div>
            <label></label>
            <input />
            <div>
                <span></span>
                <ul class="neptune-list-custom"></ul>
            </div>
        </div>
    `;
    static TEXTAREA_TEMPLATE = `
        <div>
            <label></label>
            <textarea></textarea>
            <div>
                <span></span>
                <ul></ul>
            </div>
        </div>
    `;

    componentCssUrl = '/src/components/form/input/input.css';

    constructor() {
        super();
    }

    #userErrorText = '';
    #internalErrorTextUpdate = false;
    #onInput = null;
    #onChange = null;
    #onBlur = null;

    get value() {
        const input = this.inputElement || this.shadowRoot?.querySelector('input, textarea');
        if (!this.inputElement && input) {
            this.inputElement = input;
        }
        return this.getAttribute('value') ?? input?.value ?? '';
    }

    set value(next) {
        const normalized = next == null ? '' : String(next);
        const input = this.inputElement || this.shadowRoot?.querySelector('input, textarea');
        if (!this.inputElement && input) {
            this.inputElement = input;
        }
        if (input && input.value !== normalized) {
            input.value = normalized;
        }
        if (this.getAttribute('value') !== normalized) {
            this.setAttribute('value', normalized);
        }
    }

    async connectedCallback() {
        const type = this.getAttribute('type') || 'text';
        if (type === 'textarea') {
            this.templateMarkup = Input.TEXTAREA_TEMPLATE;
        } else {
            this.templateMarkup = Input.INPUT_TEMPLATE;
        }

        await super.connectedCallback();

        this.validateRequiredProperties(['type']);
        this.inputElement = this.shadowRoot.querySelector('input, textarea');

        this.#userErrorText = (this.getAttribute('error-text') || '').trim();

        const input = this.inputElement;
        if (input) {
            if (input.tagName.toLowerCase() === 'input') {
                input.setAttribute('type', type);
            }
            Input.observedAttributes.forEach(attr => {
                this.processAttribute(attr, null, this.getAttribute(attr));
            });
            this.handleAccessibilityAttributes(input);
        }

        this.processInitialAttributesAfterRender(Input.observedAttributes);
    }

    setEventListeners() {
        if (this.#onInput || this.#onChange || this.#onBlur) {
            return;
        }

        const input = this.inputElement || this.shadowRoot?.querySelector('input, textarea');
        if (!this.inputElement && input) {
            this.inputElement = input;
        }
        if (!input) {
            return;
        }

        this.#onInput = () => {
            const type = this.getAttribute('type') || 'text';
            if (type === 'number' && this.inputElement?.validity?.badInput) {
                return;
            }
            const val = this.inputElement?.value ?? '';
            if (this.getAttribute('value') !== val) {
                this.setAttribute('value', val);
            }
        };

        this.#onChange = () => {
            const val = this.inputElement?.value ?? '';
            if (this.getAttribute('value') !== val) {
                this.setAttribute('value', val);
            }
            this.dispatchEvent(new CustomEvent('change', {
                detail: { value: val },
                bubbles: true,
                composed: true
            }));
        };

        this.#onBlur = () => this.validateInput();

        input.addEventListener('input', this.#onInput);
        input.addEventListener('change', this.#onChange);
        input.addEventListener('blur', this.#onBlur);
    }

    removeEventListeners() {
        const input = this.inputElement;
        if (!input) {
            return;
        }
        if (this.#onInput) {
            input.removeEventListener('input', this.#onInput);
        }
        if (this.#onChange) {
            input.removeEventListener('change', this.#onChange);
        }
        if (this.#onBlur) {
            input.removeEventListener('blur', this.#onBlur);
        }

        this.#onInput = null;
        this.#onChange = null;
        this.#onBlur = null;
    }

    static get observedAttributes() {
        return ['type', 'label', 'placeholder', 'value', 'disabled', 'error', 'required', 'supporting-text', 'min', 'max', 'step', 'readonly', 'maxlength', 'minlength', 'pattern', 'autocomplete', 'error-text', 'name'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        const input = this.inputElement || this.shadowRoot?.querySelector('input, textarea');
        if (!this.inputElement && input) {
            this.inputElement = input;
        }
        if (!input) return;

        const type = this.getAttribute('type') || 'text';

        switch (name) {
            case 'placeholder': {
                if (Input.PLACEHOLDER_TYPES.includes(type) && newValue !== null) {
                    input.setAttribute('placeholder', newValue);
                } else {
                    input.removeAttribute('placeholder');
                }
                break;
            }
            case 'value': {
                input.value = newValue || '';
                if (input.hasAttribute('pattern') && newValue !== null && newValue !== '') {
                    this.validateInput();
                }
                break;
            }
            case 'disabled': {
                this.hasAttribute('disabled')
                    ? input.setAttribute('disabled', '')
                    : input.removeAttribute('disabled');
                break;
            }
            case 'name': {
                newValue !== null
                    ? input.setAttribute('name', newValue)
                    : input.removeAttribute('name');
                break;
            }
            case 'required': {
                this.hasAttribute('required')
                    ? input.setAttribute('required', '')
                    : input.removeAttribute('required');
                break;
            }
            case 'readonly': {
                this.hasAttribute('readonly')
                    ? input.setAttribute('readonly', '')
                    : input.removeAttribute('readonly');
                break;
            }
            case 'error': {
                this.hasAttribute('error')
                    ? input.setAttribute('aria-invalid', 'true')
                    : input.removeAttribute('aria-invalid');
                this.updateMessages();
                break;
            }
            case 'min': {
                this.handleMinMaxStepAttributes(input, type);
                break;
            }
            case 'max': {
                this.handleMinMaxStepAttributes(input, type);
                break;
            }
            case 'step': {
                this.handleMinMaxStepAttributes(input, type);
                break;
            }
            case 'maxlength': {
                this.handleLengthAttributes(input, type);
                break;
            }
            case 'minlength': {
                this.handleLengthAttributes(input, type);
                break;
            }
            case 'pattern': {
                this.handlePatternAttribute(input, type);
                break;
            }
            case 'autocomplete': {
                this.handleAutocompleteAttribute(input);
                break;
            }
            case 'label': {
                this.setLabel();
                break;
            }
            case 'supporting-text':
            case 'error-text': {
                if (name === 'error-text' && !this.#internalErrorTextUpdate) {
                    this.#userErrorText = (newValue || '').trim();
                }
                this.updateMessages();
                break;
            }
            default: {
                break;
            }
        }
    }

    validateInput() {
        const input = this.inputElement;
        if (!input) return;

        const label = this.getAttribute('label') || 'Input';
        const type = this.getAttribute('type') || 'text';
        const inputValue = (input.value || '').trim();
        const errors = new Set();

        if (input.validity.valueMissing) {
            errors.add(`${label} is required`);
        }

        const mismatchMsg = this.#userErrorText || `Please enter a valid ${label}`;

        if (input.validity.typeMismatch || input.validity.patternMismatch || input.validity.badInput) {
            errors.add(mismatchMsg);
        }

        if (input.validity.tooShort) {
            const minLength = this.getAttribute('minlength');
            errors.add(`${label} needs at minimum ${minLength} characters`);
        }

        if (input.validity.tooLong) {
            const maxLength = this.getAttribute('maxlength');
            errors.add(`${label} cannot exceed ${maxLength} characters`);
        }

        if (input.validity.tooShort || input.validity.tooLong) {
            const minLength = this.getAttribute('minlength');
            const maxLength = this.getAttribute('maxlength');
            if (minLength && maxLength) {
                errors.add(`Value needs to be between ${minLength}-${maxLength} characters`);
            }
        }

        if (input.validity.rangeUnderflow || input.validity.rangeOverflow) {
            const min = this.getAttribute('min');
            const max = this.getAttribute('max');
            if (min && max) {
                errors.add(`Value needs to be between the numbers ${min} and ${max}`);
            } else if (input.validity.rangeUnderflow) {
                errors.add(`Value needs to be at least ${min}`);
            } else if (input.validity.rangeOverflow) {
                errors.add(`Value cannot exceed ${max}`);
            }
        }

        if (input.validity.stepMismatch) {
            const step = this.getAttribute('step');
            const min = this.getAttribute('min') || 0;
            const max = this.getAttribute('max');
            if (step && max) {
                errors.add(`Value needs to be in increments of ${step} between ${min} and ${max}`);
            } else if (step) {
                errors.add(`Value needs to be in increments of ${step}`);
            }
        }

        if (type === 'url' && inputValue && !Input.URL_HTTP_REGEX.test(inputValue)) {
            errors.add(this.#userErrorText || `Please enter a valid ${label}`);
        }

        const hasCustomPattern = !!(this.getAttribute('pattern') && this.getAttribute('pattern').trim());
        if (type === 'tel' && !hasCustomPattern && inputValue && !Input.TEL_VALUE_REGEX.test(inputValue)) {
            errors.add(this.#userErrorText || `Please enter a valid ${label}`);
        }

        const errorText = [...errors].join('|');

        this.#internalErrorTextUpdate = true;
        try {
            if (errorText) {
                this.setAttribute('error', '');
                this.setAttribute('error-text', errorText);
            } else {
                this.removeAttribute('error');
                this.removeAttribute('error-text');
            }
        } finally {
            this.#internalErrorTextUpdate = false;
        }

        this.updateMessages();
    }

    handleLengthAttributes(input, type) {
        if (!Input.MAX_LENGTH_TYPES.includes(type)) {
            input.removeAttribute('maxlength');
            input.removeAttribute('minlength');
            return;
        }
        const maxLength = this.getAttribute('maxlength');
        const minLength = this.getAttribute('minlength');
        if (maxLength !== null && minLength !== null && parseInt(maxLength) < parseInt(minLength)) {
            throw new Error(`The maxlength attribute value (${maxLength}) cannot be less than minlength attribute value (${minLength}).`);
        }
        maxLength !== null ? input.setAttribute('maxlength', maxLength) : input.removeAttribute('maxlength');
        minLength !== null ? input.setAttribute('minlength', minLength) : input.removeAttribute('minlength');
    }

    handleMinMaxStepAttributes(input, type) {
        if (!Input.MIN_MAX_STEP_TYPES.includes(type)) {
            input.removeAttribute('min');
            input.removeAttribute('max');
            input.removeAttribute('step');
            return;
        }
        const min = this.getAttribute('min');
        const max = this.getAttribute('max');
        const step = this.getAttribute('step');
        min !== null ? input.setAttribute('min', min) : input.removeAttribute('min');
        max !== null ? input.setAttribute('max', max) : input.removeAttribute('max');
        step !== null ? input.setAttribute('step', step) : input.removeAttribute('step');
    }

    handlePatternAttribute(input, type) {
        if (!Input.PATTERN_TYPES.includes(type)) {
            input.removeAttribute('pattern');
            return;
        }
        const pattern = this.getAttribute('pattern');
        const hasUserPattern = pattern && pattern.trim() !== '';

        if (hasUserPattern) {
            const errorText = this.getAttribute('error-text');
            if (!errorText || !errorText.trim()) {
                throw new Error('Pattern attribute requires an error-text attribute to be set.');
            }
            input.setAttribute('pattern', pattern.trim());
            return;
        }

        const defaultPattern = this.getDefaultPattern(type);
        if (defaultPattern) {
            input.setAttribute('pattern', defaultPattern);
            return;
        }

        input.removeAttribute('pattern');
    }

    getDefaultPattern(type) {
        if (type === 'url') {
            return Input.DEFAULT_URL_PATTERN;
        }

        if (type === 'tel') {
            return Input.DEFAULT_TEL_PATTERN;
        }

        return '';
    }

    handleAutocompleteAttribute(input) {
        const autocomplete = this.getAttribute('autocomplete');
        autocomplete !== null ? input.setAttribute('autocomplete', autocomplete) : input.setAttribute('autocomplete', 'off');
    }

    handleAccessibilityAttributes(input) {
        const inputId = `neptune-input-${Math.random().toString(36).substr(2, 9)}`;
        input.id = inputId;

        const label = this.shadowRoot.querySelector('label');
        if (label) label.setAttribute('for', inputId);

        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('span');

        if (errorList) {
            errorList.id = `neptune-input-errors-${Math.random().toString(36).substr(2, 9)}`;
        }

        if (supportingText) {
            supportingText.id = `neptune-input-support-${Math.random().toString(36).substr(2, 9)}`;
        }

        const describedBy = [errorList?.id, supportingText?.id].filter(Boolean).join(' ');
        if (describedBy) {
            input.setAttribute('aria-describedby', describedBy);
        }
    }

    setLabel() {
        const labelElement = this.shadowRoot.querySelector('label');
        if (!labelElement) {
            return;
        }
        const labelText = this.getAttribute('label') || '';
        labelElement.textContent = labelText;
    }

    updateMessages() {
        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('span');

        if (!errorList || !supportingText) return;

        errorList.setAttribute('aria-live', 'polite');
        supportingText.setAttribute('aria-live', 'polite');

        if (this.hasAttribute('error')) {
            const errorText = this.getAttribute('error-text') || '';
            const errors = errorText.split('|').filter(e => e.trim());

            errorList.innerHTML = '';
            if (errors.length > 0) {
                errors.forEach(error => {
                    const li = document.createElement('li');
                    li.textContent = error.trim();
                    errorList.appendChild(li);
                });
                errorList.style.display = 'block';
            } else {
                errorList.style.display = 'none';
            }

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            if (supportingTextValue) {
                supportingText.textContent = supportingTextValue;
                supportingText.classList.add('visually-hidden-a11y');
                supportingText.style.display = 'block';
            } else {
                supportingText.style.display = 'none';
            }
        } else {
            errorList.innerHTML = '';
            errorList.style.display = 'none';

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            supportingText.textContent = supportingTextValue;
            supportingText.classList.remove('visually-hidden-a11y');
            supportingText.style.display = supportingTextValue ? 'block' : 'none';
        }
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
    }
}

customElements.define('neptune-input', Input);

export default Input;